import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type StoredFile } from "@/lib/api";
import { useTranslations } from "@/hooks/use-translations";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SafeReportMarkdown } from "./safe-report-markdown";

function JsonNode({ name, value, depth, search }: { name: string; value: unknown; depth: number; search: string }) {
	const [open, setOpen] = useState(depth === 0);
	const [limit, setLimit] = useState(100);
	const t = useTranslations("fileReports");
	if (typeof value !== "object" || value === null) {
		return <div className="py-0.5 break-words"><span className="text-text-primary">{name}</span>: {JSON.stringify(value)}</div>;
	}
	const entries = Object.entries(value).filter(([key, child]) => !search || `${key} ${JSON.stringify(child)}`.toLowerCase().includes(search));
	return <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)} className="py-1">
		<summary className="cursor-pointer text-text-primary">{name} <span className="text-text-tertiary">{Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}</span></summary>
		{open && <div className="ml-4 border-l border-border-subtle pl-3">
			{entries.slice(0, limit).map(([key, child]) => <JsonNode key={key} name={key} value={child} depth={depth + 1} search={search} />)}
			{entries.length > limit && <button onClick={() => setLimit(limit + 100)} className="text-text-link underline underline-offset-2 py-2">{t("showMore")}</button>}
		</div>}
	</details>;
}

function JsonDocument({ content }: { content: string }) {
	const [search, setSearch] = useState("");
	const t = useTranslations("fileReports");
	const data = useMemo(() => { try { return JSON.parse(content) as unknown; } catch { return undefined; } }, [content]);
	if (data === undefined) return <pre className="whitespace-pre-wrap break-words">{content}</pre>;
	return <div className="space-y-4">
		<Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("searchJson")} />
		<div className="font-mono text-xs text-text-secondary"><JsonNode name="root" value={data} depth={0} search={search.trim().toLowerCase()} /></div>
	</div>;
}

export function formatFileSize(bytes?: number | null): string {
	if (bytes == null) return "—";
	return bytes < 1024 ? `${bytes} B` : bytes < 1024 ** 2 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

export function FilePreview({ file }: { file: StoredFile }) {
	const { user } = useAuth();
	const t = useTranslations("fileReports");
	const html = file.mimeType === "text/html";
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["file-content", user?.id, file.id], queryFn: () => api.getFileContent(file.id),
		enabled: !!user?.id && file.canPreview && !html,
	});
	if (!file.canPreview) return <p className="text-text-secondary">{t("downloadOnly")}</p>;
	if (html) return <iframe src={api.filePreviewUrl(file.id)} sandbox="" referrerPolicy="no-referrer"
		title={file.title ?? "HTML report"} className="w-full min-h-[75vh] rounded-xl bg-white" />;
	if (isLoading) return <p className="text-text-secondary">{t("loading")}</p>;
	if (isError) return <div className="space-y-3 text-danger"><p>{t("readError")}</p>
		<Button variant="secondary" onClick={() => refetch()}>{t("retry")}</Button></div>;
	if (!data) return null;
	if (file.mimeType === "application/json") return <JsonDocument content={data.content} />;
	if (file.mimeType === "text/markdown") return <div className="chat-markdown text-sm leading-relaxed"><SafeReportMarkdown>{data.content}</SafeReportMarkdown></div>;
	return <pre className="whitespace-pre-wrap break-words text-sm text-text-secondary">{data.content}</pre>;
}
